﻿import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Trash } from "lucide-react";
import { toast } from "react-toastify";

import CustomBadge from "@/components/desktop/badge/CustomBadge";
import ListScaffold from "@/components/desktop/list/table/ListScaffold";
import CustomDropdown from "@/components/desktop/list/dropdown/CustomDropdown";
import BasicConfirm from "@/components/Confirm/BasicConfirm";
import CustomFetching from "@/components/desktop/fetching/CustomFetching";
import CustomNoPermissionView from "@/components/desktop/permission/no-permission/CustomNoPermissionView";

import useListController from "@/hooks/list/useListController";
import { useScreenPermissions } from "@/hooks/general/useScreenPermissions";
import { useLookupsObjective, useObjectives, useDeleteObjective } from "@/hooks/maintainers/OA/Objectives/useObjective.js";

import useFilterStore from "@/stores/useFilterStore";
import { SCREEN_ID_OBJECTIVES } from "@/constants/screenIds";
import ObjectiveForm from "./Form/ObjectiveForm.jsx";

import { base_table } from "./objectives.constants.js";

const Objectives = () => {
	const navigate = useNavigate();
	const { filter } = useFilterStore();
	const { estate_parent_id, estate_id, search, parameter } = filter;
	const { canShow, canCreate, canEdit, canDelete, isLoading: isLoadingPermissions } = useScreenPermissions(SCREEN_ID_OBJECTIVES);
	const canLoadScreenData = !isLoadingPermissions && canShow;
	const { isLoading: isLoadingLookups } = useLookupsObjective(canLoadScreenData);

	const { tableState, appliedFilterParams, reactTableSort, handlePageChange, handlePageSizeChange, handleSortByChange } = useListController({
		baseFilter: [],
		baseTable: base_table,
	});

	const { data, isLoading } = useObjectives(
		{
			estate_id,
			estate_parent_id,
			search,
			parameter,
			...appliedFilterParams,
			...tableState,
		},
		canLoadScreenData,
	);

	const { mutate: deleteOAType, isLoading: isLoadingDelete } = useDeleteObjective();
	const [showDelete, setShowDelete] = useState(false);
	const [selected, setSelected] = useState(null);

	const handleDelete = () => {
		setShowDelete(false);

		deleteOAType(
			{
				selected,
				filters: {
					estate_id,
					estate_parent_id,
					search,
					parameter,
					...appliedFilterParams,
					...tableState,
				},
			},
			{
				onSuccess: () => {
					toast.success("Objetivos OA eliminado correctamente");
				},
				onError: () => {
					toast.error("Error al eliminar Objetivos OA");
				},
			},
		);
	};

	const columns = useMemo(
		() => [
			{
				Header: "#",
				accessor: "",
				disableSortBy: true,
				width: "5%",
				Cell: ({ row }) => {
					return <span>{(tableState.page - 1) * tableState.per_page + row.index + 1}</span>;
				},
			},
			{ Header: "Nombre", accessor: "name", width: "12%" },
			{
				Header: "Campos",
				accessor: "estates_parents",
				width: "25%",
				Cell: ({ value }) => {
					if (!value) {
						return;
					}
					return (
						<div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
							{value.map((estate_parent) => {
								return <CustomBadge key={estate_parent.value}>{estate_parent.label}</CustomBadge>;
							})}
						</div>
					);
				},
			},
			{
				Header: "Bodegas",
				accessor: "estates",
				width: "25%",
				Cell: ({ value }) => {
					if (!value) {
						return;
					}
					return (
						<div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
							{value.map((estate) => {
								return <CustomBadge key={estate.value}>{estate.label}</CustomBadge>;
							})}
						</div>
					);
				},
			},
			{
				Header: "Acción",
				disableSortBy: true,
				accessor: "id",
				width: "10%",
				Cell: ({ value }) => {
					const actions = [];

					if (canEdit) {
						actions.push({
							label: "Editar",
							action: () => navigate("/configuracion/objetivos_oa/editar/" + value),
							icon: Pencil,
						});
					}

					if (canDelete) {
						actions.push({
							label: "Eliminar",
							action: () => {
								setSelected(value);
								setShowDelete(true);
							},
							icon: Trash,
							danger: true,
						});
					}

					if (actions.length === 0) {
						return null;
					}

					return <CustomDropdown title="Acciones" items={actions} align="right" />;
				},
			},
		],
		[canDelete, canEdit, navigate, tableState.page, tableState.per_page],
	);

	if (isLoadingPermissions) return <CustomFetching center message="Validando permisos..." />;
	if (!canShow) return <CustomNoPermissionView />;

	return (
		<ListScaffold
			titleBreadcrumbs={[
				{ label: "Dashboard", onClick: () => navigate("/") },
				{ label: "Configuración", onClick: () => navigate("/configuracion?section=orderApplication") },
				{ label: "Objetivos OA", onClick: () => navigate("/configuracion/objetivos_oa") },
			]}
			subtitle="Personaliza el nombre de cada Objetivo OA y reutiliza su configuración"
			showCreate={canCreate}
			createLabel="Nuevo Objetivo OA"
			onCreate={canCreate ? () => navigate("/configuracion/objetivos_oa/crear") : undefined}
			actionButtons={[]}
			isLoading={isLoading || isLoadingDelete || isLoadingLookups}
			data={data?.data || []}
			totalItems={data?.total || 0}
			totalPages={data?.total_pages || 0}
			toItem={data?.to || 0}
			fromItem={data?.from || 0}
			pageIndex={tableState.page}
			columns={columns}
			onPageChange={handlePageChange}
			pageSize={tableState.per_page}
			onPageSizeChange={handlePageSizeChange}
			sortBy={reactTableSort}
			onSortByChange={handleSortByChange}
			pageSizeOptions={[10, 20, 50]}
			initialPageSize={tableState.per_page}
			initialSortBy={reactTableSort}
		>
			<ObjectiveForm
				filters={{
					estate_id,
					estate_parent_id,
					search,
					parameter,
					...appliedFilterParams,
					...tableState,
				}}
			/>

			<BasicConfirm
				showConfirm={showDelete}
				setShowConfirm={setShowDelete}
				handleAction={handleDelete}
				showValidation={true}
				textValidation={"eliminar Objetivo OA"}
			/>
		</ListScaffold>
	);
};

export default Objectives;
